import './styles.scoped.css'
import {useEffect, useRef, useState} from 'react'
import {Box, Tabs, Tab, Paper, Typography, Avatar, Stack, Button} from '@mui/material'
import {ChevronRightRounded, DateRange, AccessTime} from '@mui/icons-material'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime} from '../../../../utils/helpers'
import {format} from 'date-fns'
import {useMobile} from '../../../../hooks'
import {dashboardApi, scheduleApi} from '../../../../services'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {canMarkAsComplete} from '../../../../utils/visitActions'
import {VisitCardsSkeleton} from '../DashboardSkeletons'
import {CancelVisits, UndoClockIn, MarkAsComplete, ClockOut} from '../../../../components'

export default () => {
    const {mobile} = useMobile()
    const {setMessageBox, branchId, dashboardDate, dashboardRefresh} = useGlobalStates()
    const [items, setItems] = useState(null)
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const [openMarkAsComplete, setOpenMarkAsComplete] = useState(false)
    const [openCancelVisits, setOpenCancelVisits] = useState(false)
    const [openClockOut, setOpenClockOut] = useState(false)
    const [openUndoClockIn, setOpenUndoClockIn] = useState(false)
    const [visit, setVisit] = useState(null)
    const [visitLoading, setVisitLoading] = useState(false)
    const visitRequestId = useRef(0)

    // A new day, branch or Refresh replaces the list with the skeleton.
    useEffect(() => {
        setSkeleton(true)
        setItems(null)
    }, [branchId, dashboardDate, dashboardRefresh])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (branchId && dashboardDate) {
                const id = ++requestId.current
                dashboardApi
                    .getTodaysScheduleOverview(branchId, {date: format(dashboardDate, 'yyyy-MM-dd'), page_size: 100})
                    .then(
                        ({
                            data: {
                                data: {visits},
                            },
                        }) => {
                            if (id !== requestId.current) return
                            // setItems(visits)
                            setSkeleton(false)
                        },
                    )
                    .catch(error => {
                        if (id !== requestId.current) return
                        const {title, description} = parseError(error)
                        if (description) {
                            setMessageBox({status: 'error', title, description})
                        }
                        setSkeleton(false)
                    })
            }
        }, 500)
        return () => clearTimeout(timeout)
    }, [branchId, dashboardDate, dashboardRefresh])

    const getScheduleVisit = (scheduleId, visitId) => {
        if (branchId) {
            const id = ++visitRequestId.current
            setVisitLoading(true)
            scheduleApi
                .getScheduleVisit(branchId, scheduleId, visitId)
                .then(({data}) => {
                    if (id !== visitRequestId.current) return
                    setVisitLoading(false)
                    setVisit(data)
                })
                .catch(error => {
                    if (id !== visitRequestId.current) return
                    setVisitLoading(false)
                    setOpenMarkAsComplete(false)
                    setOpenCancelVisits(false)
                    setOpenClockOut(false)
                    setOpenUndoClockIn(false)
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }

    const loadData = () => {}

    const markAsComplete = item => {
        setOpenMarkAsComplete(true)
        setVisit(null)
        getScheduleVisit(item?.schedule_id, item?.id)
    }

    const cancelSchedule = item => {
        setOpenCancelVisits(true)
        setVisit(null)
        getScheduleVisit(item?.schedule_id, item?.id)
    }

    const recordClockOut = item => {
        setOpenClockOut(true)
        setVisit(null)
        getScheduleVisit(item?.schedule_id, item?.id)
    }

    const undoClockIn = item => {
        setOpenUndoClockIn(true)
        setVisit(null)
        getScheduleVisit(item?.schedule_id, item?.id)
    }

    return (
        <div className="container">
            {skeleton && <VisitCardsSkeleton mobile={mobile} badge={56} buttons={[80, 70, 150, 100]} mobileButtons={[2, 2]} />}
            {items?.map((item, index) => (
                <div key={index}>
                    <br />
                    {mobile ? (
                        <div className="card">
                            <Typography variant="h8" color="text.primary">
                                {item?.service_name}
                            </Typography>
                            <br />
                            <br />
                            <div className="datetime">
                                <div className="row-content">
                                    <Typography variant="body3" color="text.secondary">
                                        <DateRange fontSize="small" />
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary">
                                        {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                    </Typography>
                                </div>
                                <Typography variant="body3" color="text.secondary">
                                    |
                                </Typography>
                                <div className="row-content">
                                    <Typography variant="body3" color="text.secondary">
                                        <AccessTime fontSize="small" />
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary">
                                        {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                    </Typography>
                                </div>
                            </div>
                            <Typography variant="overline" color="primary">
                                2:12:56
                            </Typography>
                            <br />
                            <Typography variant="caption" color="primary">
                                CLIENT
                            </Typography>
                            <div className="people-card">
                                {item?.client?.avatar_url ? (
                                    <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.client?.name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.client?.name)}
                                    </Avatar>
                                )}
                                <div className="people-profile-card">
                                    <Typography variant="h8" color="text.primary" className="name">
                                        {item?.client?.name}
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary" className="name">
                                        {item?.client?.email}
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <Typography variant="caption" color="primary">
                                CAREGIVER
                            </Typography>
                            <div className="people-card">
                                {item?.caregiver?.avatar_url ? (
                                    <Avatar src={item?.caregiver?.avatar_url} style={{width: 40, height: 40}} />
                                ) : (
                                    <Avatar
                                        style={{
                                            backgroundColor: getColorFromName(item?.caregiver?.name),
                                            width: 40,
                                            height: 40,
                                            fontSize: 20,
                                        }}>
                                        {getInitials(item?.caregiver?.name)}
                                    </Avatar>
                                )}
                                <div className="people-profile-card">
                                    <Typography variant="h8" color="text.primary" className="name">
                                        {item?.caregiver?.name}
                                    </Typography>
                                    <Typography variant="body3" color="text.secondary" className="name">
                                        {item?.caregiver?.email}
                                    </Typography>
                                </div>
                            </div>
                            <br />
                            <div className="actions">
                                <div className="actions-top">
                                    {canMarkAsComplete(item?.status) && (
                                        <Button variant="outlined" size="small" fullWidth onClick={() => markAsComplete(item)}>
                                            Mark as Complete
                                        </Button>
                                    )}
                                    <Button variant="contained" size="small" fullWidth onClick={() => recordClockOut(item)}>
                                        Clock Out
                                    </Button>
                                </div>
                                <div className="actions-bottom">
                                    <Button variant="outlined" color="error" size="small" fullWidth onClick={() => cancelSchedule(item)}>
                                        Cancel
                                    </Button>
                                    <Button variant="outlined" color="error" size="small" fullWidth onClick={() => undoClockIn(item)}>
                                        Undo
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card">
                            <div className="card-top">
                                <Typography variant="h8" color="text.primary">
                                    {item?.service_name}
                                </Typography>
                                <div className="card-top-right">
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                    <Typography variant="overline" color="primary">
                                        2:12:56
                                    </Typography>
                                </div>
                            </div>
                            <div className="card-bottom">
                                <div className="people-container">
                                    <div>
                                        <Typography variant="caption" color="primary">
                                            CLIENT
                                        </Typography>
                                        <div className="people-card">
                                            {item?.client?.avatar_url ? (
                                                <Avatar src={item?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.client?.name),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.client?.name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.client?.name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.client?.email}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <Typography variant="caption" color="primary">
                                            CAREGIVER
                                        </Typography>
                                        <div className="people-card">
                                            {item?.caregiver?.avatar_url ? (
                                                <Avatar src={item?.caregiver?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.caregiver?.name),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.caregiver?.name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.caregiver?.name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.caregiver?.email}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="actions">
                                    <Button variant="outlined" color="error" onClick={() => cancelSchedule(item)}>
                                        Cancel
                                    </Button>
                                    <Button variant="outlined" color="error" onClick={() => undoClockIn(item)}>
                                        Undo
                                    </Button>
                                    {canMarkAsComplete(item?.status) && (
                                        <Button variant="outlined" onClick={() => markAsComplete(item)}>
                                            Mark as Complete
                                        </Button>
                                    )}
                                    <Button variant="contained" onClick={() => recordClockOut(item)}>
                                        Clock Out
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}
            <MarkAsComplete open={openMarkAsComplete} setOpen={setOpenMarkAsComplete} loadData={loadData} items={[visit]} loading={visitLoading} />
            <CancelVisits open={openCancelVisits} setOpen={setOpenCancelVisits} loadData={loadData} items={[visit]} loading={visitLoading} />
            <ClockOut open={openClockOut} setOpen={setOpenClockOut} loadData={loadData} items={[visit]} loading={visitLoading} />
            <UndoClockIn open={openUndoClockIn} setOpen={setOpenUndoClockIn} loadData={loadData} items={[visit]} loading={visitLoading} />
        </div>
    )
}
